import { toast } from 'react-toastify';
import { io, Socket } from 'socket.io-client';

import { ServerToClientEvents, ClientToServerEvents } from 'susy-common/models';

import { ILocalization } from '../localization/definitions/types';
import { store } from '../store/store';
import { getToken } from './authentication';

const serverUri =
    process.env.REACT_APP_SUSY_API === undefined
        ? 'localhost:5000'
        : process.env.REACT_APP_SUSY_API;

export const connectToWebSocket = async (): Promise<
    Socket<ServerToClientEvents, ClientToServerEvents>
> => {
    const localization: ILocalization = store.getState().localization;
    const sidebarLocalization =
        localization.localizationStrings.susy.project.projectTopic.sidebar;
    const token = await getToken();
    if (!token) {
        toast.info(sidebarLocalization.failedToRetrieveToken);
    }
    const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(
        serverUri,
        {
            autoConnect: false, // Connect manually via 'socket.connect()'
            reconnection: true,
            auth: {
                token,
            },
        }
    );

    // https://socket.io/docs/v4/client-socket-instance/#connect_error
    socket.on('connect_error', (err) => {
        console.warn(
            `${sidebarLocalization.chatError} '${err.message}'\n${sidebarLocalization.reconnecting}`,
            {
                containerId: 'chat_connect_error',
            }
        );
        setTimeout(() => {
            socket.connect();
        }, 1000);
    });

    // https://socket.io/docs/v4/client-socket-instance/#disconnect
    socket.on('disconnect', (reason) => {
        console.warn(
            `${
                sidebarLocalization.disconnectedFromChatDueTo
            } '${getLocalizedWebSocketDisconnectReason(
                reason,
                localization
            )}'\n${sidebarLocalization.reconnecting}`,
            {
                containerId: 'chat_disconnect',
            }
        );
        if (reason === WebSocketDisconnectReason.IOServerDisconnect) {
            // the disconnection was initiated by the server, you need to reconnect manually
            socket.connect();
        }
    });

    socket.connect();
    return socket;
};

export const removeListeners = (
    socket: Socket<ServerToClientEvents, ClientToServerEvents> | undefined
): void => {
    if (socket) {
        socket.off('message');
        socket.off('newTopicMessageNotification');
        socket.off('newTopicNotification');
        socket.off('disconnect');
        socket.off('connect_error');
    }
};

// Table under: https://socket.io/docs/v4/client-socket-instance/#disconnect
// In 'io server disconnect' (explicit disconnection), the client will not try to reconnect, and you need to manually call socket.connect().
// In all other cases, the client will wait for a small random delay and then try to reconnect.
enum WebSocketDisconnectReason {
    IOServerDisconnect = 'io server disconnect',
    PingTimeout = 'ping timeout',
    TransportClose = 'transport close',
    TransportError = 'transport error',
}

export const getLocalizedWebSocketDisconnectReason = (
    webSocketDisconnectReason: string,
    localization: ILocalization
): string => {
    let retVal = '';
    switch (webSocketDisconnectReason) {
        case WebSocketDisconnectReason.IOServerDisconnect: {
            retVal =
                localization.localizationStrings.susy.project.projectTopic
                    .webSocketDisconnectReason.ioServerDisconnect;
            break;
        }
        case WebSocketDisconnectReason.PingTimeout: {
            retVal =
                localization.localizationStrings.susy.project.projectTopic
                    .webSocketDisconnectReason.pingTimeout;
            break;
        }
        case WebSocketDisconnectReason.TransportClose: {
            retVal =
                localization.localizationStrings.susy.project.projectTopic
                    .webSocketDisconnectReason.transportClose;
            break;
        }
        case WebSocketDisconnectReason.TransportError: {
            retVal =
                localization.localizationStrings.susy.project.projectTopic
                    .webSocketDisconnectReason.transportError;
            break;
        }
    }
    return retVal;
};
